import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { loadArticles } from "../../../api/articles";
import { truncateAtLastSpace } from "../../../utils/textUtils";
import ExternalArticleModal from "../articles/ExternalArticleModal";
import "../../../assets/css/animate.css";
import "./articlesArea.css";

const ArticlesArea = () => {

  const ARTICLE_PAGE_SIZE = 5;
  const MAX_DISPLAY_AGE_DAYS = 30;
  const getSourceLabel = (source) => source || "Externe";
  const getSourceBadgeClass = (source) => {
    const normalized = String(source || "").toLowerCase();
    if (normalized === "islamweb") return "source-islamweb";
    if (normalized === "islamhouse") return "source-islamhouse";
    return "source-default";
  };

  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const [externalModal, setExternalModal] = useState({ isOpen: false, url: "", title: "" });

  const filterRecentByDisplayDate = (items) => {
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - MAX_DISPLAY_AGE_DAYS);
    return (items || []).filter((article) => {
      const rawDate = new Date(article.date);
      return !Number.isNaN(rawDate.getTime()) && rawDate >= cutoff;
    });
  };

  useEffect(() => {
    let isMounted = true;

    const loadLastArticles = async () => {
      try {
        const loadedArticles = await loadArticles(true, ARTICLE_PAGE_SIZE);
        if (!isMounted) return;

        const filteredArticles = filterRecentByDisplayDate(loadedArticles);
        setArticles(filteredArticles);
        setCategories([...new Set(filteredArticles.map((article) => article.category))].sort());
      } catch (error) {
        if (isMounted) {
          console.error('Failed to load articles:', error);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadLastArticles();

    return () => {
      isMounted = false;
    };
  }, []);

  const selectCategory = (category) => setActiveCategory(category);

  const openExternalArticle = (article) => {
    if (!article?.sourceUrl) return;
    setExternalModal({
      isOpen: true,
      url: article.sourceUrl,
      title: article.title,
    });
  };

  const closeExternalArticle = () => {
    setExternalModal({ isOpen: false, url: "", title: "" });
  };

  const displayedArticles = activeCategory
    ? articles.filter(a => a.category === activeCategory)
    : articles;

  return (
    <>
      {loading ? (
        <div>Loading...</div>
      ) : (
        <div className="blog-area gray-300 home-articles">
          <div className="container position-relative pt-110">
            <div className="row">
              <div className="col-lg-12">
                <div className="section-head">
                  <h5>Articles</h5>
                  <h3>Derniers articles</h3>
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col-lg-4 pt-24">
                <div
                  className="blog-category-wrap wow fadeInLeft  animated "
                  data-wow-delay="200ms"
                  data-wow-duration="1500ms"
                >
                  <ul className="categoryes">
                    <li key="all">
                      <button
                        type="button"
                        className={`category-link ${activeCategory === null ? 'active' : ''}`}
                        aria-pressed={activeCategory === null}
                        onClick={() => selectCategory(null)}
                      >
                        Tous
                      </button>
                    </li>
                    {categories.map((category, index) => (
                      <li key={index}>
                        <button
                          type="button"
                          className={`category-link ${activeCategory === category ? 'active' : ''}`}
                          aria-pressed={activeCategory === category}
                          onClick={() => selectCategory(category)}
                        >
                          {category}
                        </button>
                      </li>
                    ))}
                  </ul>
                  <div className="view-blog-btn">
                    <Link to={`${process.env.PUBLIC_URL}/articles`}>Voir tout</Link>
                  </div>
                </div>
              </div>
              {displayedArticles.length === 0 && (
                <div className="col-lg-8 col-md-6">
                  <p style={{ marginTop: '20px' }}>Aucun article pour cette catégorie.</p>
                </div>
              )}
              {displayedArticles.map((article) => (
                <div
                  key={article.id}
                  className="col-lg-4 col-md-6 wow fadeInUp animated"
                  data-wow-delay="200ms"
                  data-wow-duration="1500ms"
                >
                  <div className="blog-card-md">
                    <div className="blog-thumb">
                      {article.isExternal ? (
                        <button
                          type="button"
                          onClick={() => openExternalArticle(article)}
                          style={{ border: "none", background: "transparent", padding: 0, width: "100%" }}
                        >
                          <img className="article-image" src={article.image} alt="Imgs" />
                        </button>
                      ) : (
                        <Link to={`${process.env.PUBLIC_URL}/article-details?aid=${article.id}`}>
                          <img className="article-image" src={article.image} alt="Imgs" />
                        </Link>
                      )}
                      <div className={`article-source-badge-home ${getSourceBadgeClass(article.source)}`}>{getSourceLabel(article.source)}</div>
                      <div className="blog-tags">
                        <Link to={`${process.env.PUBLIC_URL}/articles`}>
                          {article.category}
                        </Link>
                      </div>
                    </div>
                    <div className="blog-content">
                      <h4 className="blog-title">
                        {article.isExternal ? (
                          <button
                            type="button"
                            onClick={() => openExternalArticle(article)}
                            style={{ border: "none", background: "transparent", padding: 0, textAlign: "left" }}
                          >
                            {truncateAtLastSpace(article.title, 64)}
                          </button>
                        ) : (
                          <Link to={`${process.env.PUBLIC_URL}/article-details?aid=${article.id}`}>
                            {truncateAtLastSpace(article.title, 64)}
                          </Link>
                        )}
                      </h4>
                      <div className="blog-bottom">
                        <div className="blog-date">
                          <p>
                            <i className="bi bi-calendar2-week" />{" "}
                            <span>
                              {new Date(article.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                            </span>
                          </p>
                        </div>
                        <div className="readme-btn">
                          {article.isExternal ? (
                            <button
                              type="button"
                              onClick={() => openExternalArticle(article)}
                              style={{ border: "none", background: "transparent", padding: 0 }}
                            >
                              Lire
                            </button>
                          ) : (
                            <Link to={`${process.env.PUBLIC_URL}/article-details?aid=${article.id}`}>
                              Lire
                            </Link>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <ExternalArticleModal
            isOpen={externalModal.isOpen}
            url={externalModal.url}
            title={externalModal.title}
            onClose={closeExternalArticle}
          />
        </div>
    )}
    </>
  );
}

export default ArticlesArea;
